/**
 * Template token substitution. The token contract is shared with the
 * templates under packages/scaffolder/templates/:
 *
 *   {{GHOSTY_SLUG}} {{GHOSTY_APP_NAME}} {{GHOSTY_DESCRIPTION}}
 *   {{GHOSTY_BASE_DOMAIN}} {{GHOSTY_ADMIN_URL}} {{GHOSTY_PRODUCT_NAME}}
 *   {{GHOSTY_REGION}} {{GHOSTY_STAMP_PROJECT}} {{GHOSTY_BRAND_COLOR}}
 *
 * Binary-safe: non-UTF-8 content is passed through untouched.
 */

/** Fallback accent when the tenant set no brand colour (the Ghosty emerald). */
export const DEFAULT_BRAND_COLOR = '#059669';

/** The {{GHOSTY_*}} values a scaffold is parameterised by. */
export interface TemplateTokens {
  GHOSTY_SLUG: string;
  GHOSTY_APP_NAME: string;
  GHOSTY_DESCRIPTION: string;
  GHOSTY_BASE_DOMAIN: string;
  GHOSTY_ADMIN_URL: string;
  GHOSTY_PRODUCT_NAME: string;
  GHOSTY_REGION: string;
  GHOSTY_STAMP_PROJECT: string;
  /** The tenant's primary brand colour (hex), baked into the app's theme. */
  GHOSTY_BRAND_COLOR: string;
}

/**
 * The ordered list of token names the templates may reference. CLI and
 * admin-app share this so the set never drifts between producers.
 */
export const TEMPLATE_TOKEN_NAMES = [
  'GHOSTY_SLUG',
  'GHOSTY_APP_NAME',
  'GHOSTY_DESCRIPTION',
  'GHOSTY_BASE_DOMAIN',
  'GHOSTY_ADMIN_URL',
  'GHOSTY_PRODUCT_NAME',
  'GHOSTY_REGION',
  'GHOSTY_STAMP_PROJECT',
  'GHOSTY_BRAND_COLOR',
] as const satisfies ReadonlyArray<keyof TemplateTokens>;

const strictDecoder = new TextDecoder('utf-8', { fatal: true });

/** True when the buffer is valid UTF-8 text without NUL bytes. */
export function isUtf8Text(buf: Uint8Array): boolean {
  if (buf.includes(0)) return false;
  try {
    strictDecoder.decode(buf);
    return true;
  } catch {
    return false;
  }
}

/** Replace every known {{GHOSTY_*}} token; unknown tokens are left intact. */
export function substituteString(content: string, tokens: TemplateTokens): string {
  return content.replace(/\{\{(GHOSTY_[A-Z_]+)\}\}/g, (match, name: string) => {
    const value = (tokens as unknown as Record<string, string | undefined>)[name];
    return value !== undefined ? value : match;
  });
}

/**
 * Substitute tokens in a file buffer. Non-UTF-8 (binary) buffers are
 * returned unchanged.
 */
export function substituteBuffer(buf: Buffer, tokens: TemplateTokens): Buffer {
  if (!isUtf8Text(buf)) return buf;
  return Buffer.from(substituteString(buf.toString('utf8'), tokens), 'utf8');
}
